import React from 'react';

import { faSpinnerThird } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DialogTitle } from '@radix-ui/react-dialog';
import { useIntl } from 'react-intl';

import { LIST_PAY_STATION_OPERATIONS_RESOURCE } from 'src/features/payment/api/useListPayStationOperations';
import { useRestartPayStation } from 'src/features/payment/api/useRestartPayStation';
import { paymentsMessages } from 'src/features/payment/utils/messages';
import { PayStation } from 'src/features/payment/utils/types';
import WSContext, { WebSocketGroup } from 'src/features/visitor/context/WSContext';
import { WebSocketEvent, WebSocketEventMessages } from 'src/features/visitor/types';
import { queryClient } from 'src/lib/react-query';
import { useParking } from 'src/providers/ParkingProvider';
import ConfirmationModal from 'src/registry/components/blocks/ConfirmationModal';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { Button } from 'src/registry/ui/Button';
import { Dialog, DialogBody, DialogSeparator, DialogContent, DialogFooter, DialogHeader } from 'src/registry/ui/Dialog';
import { ICON_MAP } from 'src/utils/constants';

interface CreatePayStationApiKeyFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedPayStation: PayStation;
    children?: React.ReactNode;
}

enum RestartStatus {
    LOADING,
    SUCCESS,
    ERROR,
}
export default function RestartPayStationFormModal<TData>({
    selectedPayStation,
    children,
}: CreatePayStationApiKeyFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const [restartStatus, setRestartStatus] = React.useState<RestartStatus | null>(null);

    const { formatMessage } = useIntl();

    const { parkingId } = useParking();
    const { mutate: restartPayStation } = useRestartPayStation({
        onSuccess: () => {
            setRestartStatus(RestartStatus.LOADING);
            setIsModalOpen(false);
        },
        onError: () => setRestartStatus(RestartStatus.ERROR),
    });
    const { subscribe, unsubscribe } = React.useContext(WSContext);

    const handleWebSocket = React.useCallback(
        (event: WebSocketEvent) => {
            if (event.message !== WebSocketEventMessages.PAY_STATION_RESTART_FINISHED) return;
            if (event.data.succeeded) {
                setRestartStatus(RestartStatus.SUCCESS);
                void queryClient.invalidateQueries({ queryKey: [LIST_PAY_STATION_OPERATIONS_RESOURCE] });
            } else setRestartStatus(RestartStatus.ERROR);
        },
        [setRestartStatus],
    );

    React.useEffect(() => {
        subscribe(WebSocketGroup.PAY_STATION, handleWebSocket);

        return () => unsubscribe(WebSocketGroup.PAY_STATION, handleWebSocket);
    }, [handleWebSocket, subscribe, unsubscribe]);

    const handleTimeoutError = React.useCallback(() => {
        if (restartStatus !== RestartStatus.LOADING) return;
        setRestartStatus(RestartStatus.ERROR);
    }, [setRestartStatus, restartStatus]);

    React.useEffect(() => {
        if (restartStatus !== RestartStatus.LOADING) return;
        setTimeout(handleTimeoutError, 1000 * 60 * 2);
    }, [handleTimeoutError, restartStatus, setRestartStatus]);

    return (
        <>
            <ConfirmationModal
                isModalOpen={isModalOpen}
                setIsModalOpen={setIsModalOpen}
                dialogDetails={{
                    title: paymentsMessages.restartMachine,
                    subTitle: paymentsMessages.restartPayStation,
                    icon: ICON_MAP.RETRY,
                    confirmationMessage: paymentsMessages.confirmRestartMachine,
                }}
                callbackButton={{
                    variant: 'default',
                    buttonTitle: paymentsMessages.restartMachine,
                    callbackFn: () =>
                        restartPayStation({
                            parkingId,
                            payStationId: selectedPayStation.id,
                        }),
                }}
            >
                {children}
            </ConfirmationModal>
            <Dialog open={restartStatus !== null}>
                <DialogContent showCloseButton={false}>
                    {restartStatus === RestartStatus.LOADING ? (
                        <DialogBody className="flex flex-1 items-center justify-center">
                            <FontAwesomeIcon icon={faSpinnerThird} className="animate-spin text-6xl" />
                            {formatMessage(paymentsMessages.loadingRestartMachine)}
                        </DialogBody>
                    ) : (
                        <DialogHeader className="flex-row items-center gap-2">
                            <SquareIcon
                                icon={restartStatus === RestartStatus.SUCCESS ? ICON_MAP.CHECK : ICON_MAP.DANGER}
                            />
                            <DialogTitle className="text-base">
                                {restartStatus === RestartStatus.SUCCESS &&
                                    formatMessage(paymentsMessages.successRestartMachine)}
                                {restartStatus === RestartStatus.ERROR &&
                                    formatMessage(paymentsMessages.errorRestartMachine)}
                            </DialogTitle>
                        </DialogHeader>
                    )}
                    {restartStatus !== RestartStatus.LOADING && (
                        <>
                            <DialogSeparator />
                            <DialogFooter>
                                <Button type="button" className="flex-1" onClick={() => setRestartStatus(null)}>
                                    {formatMessage(paymentsMessages.continue)}
                                </Button>
                            </DialogFooter>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
}
